import { Fragment, useEffect, useState } from 'react';
import {
  DevHubButton,
  DevHubCard,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubLink,
  DevHubPopover,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { DocumentNode } from 'graphql';
import { CloudAccountType } from '../types/CloudAccountTypes';
import { UpdateCloudAccount } from './UpdateCloudAccount';

/**
 * This is a component that displays a list of apps in a tile format
 * @param props the app, appId, color, and subHeader to display
 */
export const CloudAccountTile = (props: {
  applicationId: number;
  appName: string | undefined;
  cloudAccount: CloudAccountType;
  cloudAccountsQuery: DocumentNode;
  userAccounts: CloudAccountType[];
  disabled: boolean;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState(false);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [appAnchorEl, setAppAnchorEl] = useState<null | HTMLElement>(null);
  const popoverOpen = Boolean(anchorEl);
  const appPopoverOpen = Boolean(appAnchorEl);
  const permissionPopoverId = popoverOpen ? 'permission-popover' : undefined;
  const appPopoverId = appPopoverOpen ? 'apps-list-popover' : undefined;

  const associatedApplications = (props?.cloudAccount?.applications ?? [])
    ?.map(app => app.applicationId)
    .sort((a, b) => a - b);
  const baseUrl = window.location.origin;

  const serviceRequestJiraLink =
    'https://tkts.sys.comcast.net/proforma/projects/APPMGMT/create-issue?ac.form.id=1&ac.issuetype.id=16517';

  useEffect(() => {
    if (
      props?.userAccounts?.find(item => item?.id === props?.cloudAccount?.id)
    ) {
      if (!props.disabled) {
        setCanDelete(true);
      }
    }
  }, [props.cloudAccount.id, props.disabled, props.userAccounts]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setSubmitted(false);
    setOpen(false);
  };

  const handleOpenLink = () => {
    const url = `https://onecloud.comcast.net/mgmt/accounts/${props?.cloudAccount?.id}`;
    window.open(url, '_blank');
  };

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleOpenAppPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAppAnchorEl(event.currentTarget);
  };

  const handleCloseAppPopover = () => {
    setAppAnchorEl(null);
  };

  const displayedApps = associatedApplications?.slice(0, 3) || [];
  const hasMoreApps =
    associatedApplications && associatedApplications.length > 3;
  const isFreewheel =
    props?.cloudAccount?.source?.toUpperCase() === 'FREEWHEEL';

  return (
    <Fragment>
      <DevHubCard
        title={props?.cloudAccount?.name}
        subTitle={props?.cloudAccount?.platform?.toUpperCase()}
        density="compact"
        consistentHeight
        actionItem={
          canDelete ? (
            <DevHubIconButton onClick={handleOpen}>
              <DevHubDynamicIcon iconName="Edit" color="primary" />
            </DevHubIconButton>
          ) : (
            <DevHubIconButton
              onClick={openPopover}
              aria-describedby={permissionPopoverId}
            >
              <DevHubDynamicIcon iconName="Lock" color="action" />
            </DevHubIconButton>
          )
        }
        bodyPosition="middle"
        cardButtonPosition="bottom"
        cardButton={
          !isFreewheel ? (
            <DevHubButton
              buttonColor="black"
              height="small"
              variant="contained"
              onClick={handleOpenLink}
              fullWidth
            >
              View on OneCloud
            </DevHubButton>
          ) : undefined
        }
        body={
          <Fragment>
            <DevHubTypography gutterBottom variant="body2">
              Cloud Account ID:{' '}
              <span>{props?.cloudAccount?.id?.split('-')[0]}</span>
            </DevHubTypography>
            <DevHubTypography gutterBottom variant="body2">
              Status: <span>{props?.cloudAccount?.status?.toUpperCase()}</span>
            </DevHubTypography>
            {isFreewheel && (
              <DevHubTypography gutterBottom variant="body2">
                Source:{' '}
                <DevHubChip
                  label="FreeWheel"
                  chipSize="small"
                  chipColor="blue"
                />
              </DevHubTypography>
            )}
            <DevHubTypography gutterBottom variant="body2">
              Associated Applications:{' '}
              {displayedApps.map((appId, index) => (
                <Fragment key={appId}>
                  <DevHubLink
                    href={`${baseUrl}/catalog/default/system/${appId}/resources`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {appId}
                  </DevHubLink>
                  {index < displayedApps.length - 1 && ', '}
                </Fragment>
              ))}
              {hasMoreApps && (
                <Fragment>
                  , and{' '}
                  <DevHubTypography
                    weight="regular"
                    variant="button"
                    onClick={handleOpenAppPopover}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textTransform: 'none',
                    }}
                  >
                    {associatedApplications!.length - 3} more
                  </DevHubTypography>
                </Fragment>
              )}
            </DevHubTypography>

            <DevHubPopover
              id={appPopoverId}
              open={appPopoverOpen}
              anchorEl={appAnchorEl}
              onClose={handleCloseAppPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <DevHubTypography
                variant="body2"
                style={{ padding: '16px', width: '20em', textWrap: 'auto' }}
              >
                All Associated Applications: <br />
                {associatedApplications?.map((appId, index) => (
                  <Fragment key={appId}>
                    <DevHubLink
                      href={`${baseUrl}/catalog/default/system/${appId}/resources`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {appId}
                    </DevHubLink>
                    {index < associatedApplications.length - 1 && ', '}
                  </Fragment>
                ))}
              </DevHubTypography>
            </DevHubPopover>
            {canDelete && (
              <UpdateCloudAccount
                appName={props?.appName}
                cloudAccountsQuery={props.cloudAccountsQuery}
                cloudAccountName={props.cloudAccount.name}
                cloudAccountId={props.cloudAccount.id}
                applicationId={props.applicationId}
                handleOpen={handleOpen}
                handleClose={handleClose}
                submitted={submitted}
                setSubmitted={setSubmitted}
                open={open}
              />
            )}
            <DevHubPopover
              id={permissionPopoverId}
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <DevHubTypography variant="body2" style={{ padding: '16px' }}>
                You don’t have permission to edit or remove the cloud account
                linked to this application.
                <br />
                To request changes, please raise a service request via{' '}
                <DevHubLink
                  href={serviceRequestJiraLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Jira
                </DevHubLink>
                .
              </DevHubTypography>
            </DevHubPopover>
          </Fragment>
        }
      />
    </Fragment>
  );
};
